import { useState, useEffect, useMemo } from 'react';
import { useKlassen } from '../contexts/KlassenContext';
import { KlasseFachEintrag } from '../types';

export function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) && /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
}

export function isStandalone(): boolean {
  return (window.navigator as any).standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * Escape-Taste. `seite: true` für Seiten (z. B. Schülerprofil schließen): dann gehört Escape
 * zuerst einem offenen Dialog/Sheet — sonst schloss Escape das Sheet UND verließ die Seite
 * dahinter (Welle P1b/P1c). Fenster selbst rufen den Hook ohne Option auf (wie bisher).
 */
export function useEscapeKey(onEscape: () => void, opts: { seite?: boolean } = {}): void {
  const seite = !!opts.seite;
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (seite && document.querySelector('[role="dialog"], [aria-modal="true"]')) return;
      onEscape();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onEscape, seite]);
}

// Welle B1: Klassen natürlich sortiert (5a, 7b, 10c statt 10c, 5a, 7b); Klassen mit eigener
// Klassenleitung (irgendeine Zeile mit rolle 'klassenleiter') stehen vorn.
export function vergleicheKlassen(a: string, b: string, aLeitung = false, bLeitung = false): number {
  return (Number(bLeitung) - Number(aLeitung)) || a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' });
}

/** Eine Zeile je Klasse, natürlich sortiert, Klassenleitung vorn (Welle B1; B3: auch ohne Hook nutzbar) */
export function eindeutigeKlassen(meineKlassen: KlasseFachEintrag[]): KlasseFachEintrag[] {
  const seen = new Map<number, KlasseFachEintrag>();
  for (const k of meineKlassen) {
    if (!seen.has(k.klasse_id)) seen.set(k.klasse_id, k);
  }
  const leitung = new Set(meineKlassen.filter(k => k.rolle === 'klassenleiter').map(k => Number(k.klasse_id)));
  return Array.from(seen.values()).sort((a, b) =>
    vergleicheKlassen(a.klasse_name, b.klasse_name, leitung.has(Number(a.klasse_id)), leitung.has(Number(b.klasse_id))));
}

export function useEindeutigeKlassen(): KlasseFachEintrag[] {
  const { meineKlassen } = useKlassen();
  return useMemo(() => eindeutigeKlassen(meineKlassen), [meineKlassen]);
}

// Welle B1: den schwebenden Mikro-Knopf (.ld-fab) ausblenden, solange ein Bildschirm unten
// eine feste Knopfleiste zeigt (CLAUDE.md: nichts Schwebendes über Inhalt ohne Abstand).
// Zähler am body, damit sich mehrere Nutzer nicht gegenseitig aufheben; Regel in index.css.
export function useOhneMikroKnopf(aktiv = true) {
  useEffect(() => {
    if (!aktiv) return;
    const b = document.body;
    b.dataset.ohneMikroknopf = String(Number(b.dataset.ohneMikroknopf ?? 0) + 1);
    return () => {
      const rest = Number(b.dataset.ohneMikroknopf ?? 1) - 1;
      if (rest > 0) b.dataset.ohneMikroknopf = String(rest); else delete b.dataset.ohneMikroknopf;
    };
  }, [aktiv]);
}

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}
